import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format, isBefore, startOfDay } from "date-fns";
import { addWorkingDays, isNonWorkingDay, upcomingClosuresLabel } from "@/lib/workingDays";
import { uploadOrderFiles } from "@/lib/upload";
import UploadProgress from "@/components/UploadProgress";
import { it } from "date-fns/locale";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import FileDropzone from "@/components/FileDropzone";
import { StatusBadge, formatDate, OrderProgressBar, DeadlineCell } from "@/lib/orderUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Plus, CalendarIcon, Loader2, Package, FileStack, Clock, FileSignature, Box, X, LayoutGrid, List, ArrowUp, ArrowDown, ChevronsUpDown, Search, CheckCircle2, Bell, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ORDER_OPTIONS_DEFAULTS } from "@/lib/orderOptionsDefaults";
import { deepMerge } from "@/lib/siteDefaults";

const parseElements = (q) => {
  const n = parseInt(q, 10);
  return isNaN(n) ? 14 : n; // "Arcata..." / "Doppia arcata" => arcata piena => trattata come 5+
};
const deadlineMinBusinessDays = (form, options = {}) => {
  const dd = options.deadline_days || {};
  const num = (v, d) => { const n = parseInt(v, 10); return isNaN(n) ? d : n; };
  const s = `${form.design_type || ""} ${form.material || ""} ${form.macro_category || ""}`.toLowerCase();
  if (s.includes("toronto")) return num(dd.toronto, 5);
  if (form.macro_category === "Stampa 3D") return num(dd.stampa_3d, 1);
  if (form.macro_category) return parseElements(form.quantity) >= 5 ? num(dd.progettazione_5_plus, 3) : num(dd.progettazione_1_4, 2);
  return num(dd.stampa_3d, 1);
};

export default function ClientDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir(field === "deadline" ? "asc" : "desc"); }
  };
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prescrFiles, setPrescrFiles] = useState([]);
  const [attachFiles, setAttachFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({ patient_name: "", macro_category: "", color: "", notes: "" });
  const [items, setItems] = useState([{ type: "", quantity: "" }]);
  const [deadline, setDeadline] = useState();
  const [urgency, setUrgency] = useState(false);
  const [options, setOptions] = useState(ORDER_OPTIONS_DEFAULTS);
  const navigate = useNavigate();
  const { user } = useAuth();
  const ctype = user?.client_type || "laboratorio";
  const lavorazioniOptions = [...new Set([...(options.materials || []), ...(options.design_types || [])])];
  const totalElements = items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
  const addItem = () => setItems([...items, { type: "", quantity: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, key, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const perTypeMin = (() => {
    const dbt = options.deadline_by_type || {};
    const vals = items.filter((it) => it.type).map((it) => {
      const e = dbt[it.type];
      if (!e) return null;
      const v = e[ctype] ?? e.laboratorio;
      return v ? Number(v) : null;
    }).filter((v) => v != null && v > 0);
    return vals.length ? Math.max(...vals) : null;
  })();
  const minDays = perTypeMin != null ? perTypeMin : deadlineMinBusinessDays(form, options, items);
  const closures = options.closure_days || [];
  const minDate = urgency ? startOfDay(new Date()) : addWorkingDays(new Date(), minDays, closures);

  useEffect(() => {
    if (!urgency && deadline && isBefore(startOfDay(deadline), minDate)) setDeadline(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate.getTime()]);

  const selectedDesignTypes = (form.design_type || "").split(" + ").map((s) => s.trim()).filter(Boolean);
  const toggleDesignType = (d) => {
    const set = new Set(selectedDesignTypes);
    if (set.has(d)) set.delete(d); else set.add(d);
    const ordered = (options.design_types || []).filter((x) => set.has(x));
    setForm({ ...form, design_type: ordered.join(" + ") });
  };

  const selectedMaterials = (form.material || "").split(" + ").map((s) => s.trim()).filter(Boolean);
  const toggleMaterial = (m) => {
    const set = new Set(selectedMaterials);
    if (set.has(m)) set.delete(m); else set.add(m);
    const ordered = (options.materials || []).filter((x) => set.has(x));
    setForm({ ...form, material: ordered.join(" + ") });
  };

  const load = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (e) {
      toast.error("Errore nel caricamento ordini");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/order-options")
      .then(({ data }) => { if (data && Object.keys(data).length) setOptions(deepMerge(ORDER_OPTIONS_DEFAULTS, data)); })
      .catch(() => {});
  }, []);

  const reset = () => {
    setForm({ patient_name: "", macro_category: "", material: "", color: "", quantity: "", design_type: "", notes: "" });
    setDeadline(undefined);
    setPrescrFiles([]);
    setAttachFiles([]);
    setItems([{ type: "", quantity: "" }]);
    setConsent(false);
    setUrgency(false);
  };

  const uploadList = async (orderId, files, kind) => {
    await uploadOrderFiles(orderId, files, { kind, onProgress: (p, name) => setUploadProgress({ pct: Math.round(p * 100), name }) });
  };
  const [uploadProgress, setUploadProgress] = useState(null);

  const submit = async () => {
    if (!form.patient_name || !form.macro_category) {
      toast.error("Compila Nome paziente e Tipo di lavoro");
      return;
    }
    const validItems = items.filter((it) => it.type && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      toast.error("Aggiungi almeno una lavorazione con la relativa quantità");
      return;
    }
    if (!urgency && deadline && isBefore(startOfDay(deadline), minDate)) {
      toast.error(`Per questo tipo di lavoro servono almeno ${minDays} giorni lavorativi. Prima data disponibile: ${format(minDate, "dd/MM/yyyy")}`);
      return;
    }
    if (!consent) {
      toast.error("Devi confermare l'autorizzazione al trattamento dei dati del paziente e l'informativa privacy");
      return;
    }
    setSaving(true);
    try {
      const totalEl = validItems.reduce((s, it) => s + Number(it.quantity), 0);
      const payload = {
        patient_name: form.patient_name,
        macro_category: form.macro_category,
        color: form.color || null,
        items: validItems.map((it) => ({ type: it.type, quantity: Number(it.quantity) })),
        quantity: String(totalEl),
        work_type: validItems.map((it) => `${it.type} ×${it.quantity}`).join(", "),
        notes: form.notes,
        deadline: deadline ? deadline.toISOString() : null,
        urgency_requested: urgency,
        privacy_consent: consent,
      };
      const { data: order } = await api.post("/orders", payload);
      try {
        await uploadList(order.id, prescrFiles, "prescrizione");
        await uploadList(order.id, attachFiles, "allegato");
      } catch (upErr) {
        setOpen(false); reset(); load();
        toast.error(`Ordine ${order.order_number} creato, ma il caricamento dei file non è riuscito. Apri l'ordine e carica i file da lì: non ricreare il caso.`, { duration: 12000 });
        navigate(`/orders/${order.id}`);
        return;
      }
      toast.success("Ordine creato con successo");
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  const stats = {
    total: orders.length,
    lavorazione: orders.filter((o) => o.status === "in_lavorazione").length,
    completato: orders.filter((o) => o.status === "completato").length,
  };

  const sortedOrders = useMemo(() => {
    const arr = orders.filter((o) => !q || o.patient_name?.toLowerCase().includes(q.toLowerCase()) || o.order_number?.toLowerCase().includes(q.toLowerCase()));
    arr.sort((a, b) => {
      if (sortField === "deadline") {
        const da = a.deadline || "9999-12-31";
        const db = b.deadline || "9999-12-31";
        const cmp = da < db ? -1 : da > db ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      }
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      const cmp = ca < cb ? -1 : ca > cb ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [orders, q, sortField, sortDir]);

  const updatedOrders = useMemo(() => orders.filter((o) => o.has_lab_update && o.status !== "completato"), [orders]);
  const completedOrders = useMemo(() => orders.filter((o) => o.status === "completato" && o.completed_unseen), [orders]);

  const [ackingBanner, setAckingBanner] = useState(false);
  const ackBanner = async (list, endpoint) => {
    setAckingBanner(true);
    try {
      await Promise.all(list.map((o) => api.post(`/orders/${o.id}/${endpoint}`)));
      load();
    } catch (e) {
      toast.error("Errore");
    } finally {
      setAckingBanner(false);
    }
  };

  return (
    <DashboardLayout
      title="I miei ordini"
      subtitle="Crea e monitora i tuoi lavori inviati al laboratorio."
      actions={
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuovo ordine</DialogTitle>
              <DialogDescription>Compila i dati del lavoro, allega la prescrizione e i file.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Nome paziente / caso</Label>
                <Input data-testid="order-patient-input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} placeholder="es. Paziente M.R. / Caso 1234" />
              </div>
              <div className="space-y-2">
                <Label>Tipo di lavoro</Label>
                <Select value={form.macro_category} onValueChange={(v) => {
                  const noMat = (options.no_material_categories || []).includes(v);
                  setForm({ ...form, macro_category: v, material: noMat ? "" : form.material, quantity: noMat ? "" : form.quantity, design_type: noMat ? form.design_type : "" });
                }}>
                  <SelectTrigger data-testid="order-macro-select"><SelectValue placeholder="Seleziona tipo di lavoro" /></SelectTrigger>
                  <SelectContent>
                    {(options.macro_categories || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!!form.macro_category && (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Label>Lavorazioni e quantità</Label>
                  <div className="space-y-2">
                    {items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Select value={it.type} onValueChange={(v) => updateItem(i, "type", v)}>
                          <SelectTrigger data-testid={`item-type-${i}`} className="flex-1"><SelectValue placeholder="Tipo di lavorazione" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {lavorazioniOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" min={1} data-testid={`item-qty-${i}`} value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} placeholder="Q.tà" className="w-24" />
                        <Button type="button" variant="ghost" size="icon" data-testid={`item-remove-${i}`} onClick={() => removeItem(i)} disabled={items.length === 1} className="text-slate-400 hover:text-red-600 shrink-0"><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <Button type="button" variant="outline" size="sm" data-testid="add-item-button" onClick={addItem}><Plus className="h-4 w-4 mr-1.5" /> Aggiungi lavorazione</Button>
                    <span className="text-xs font-medium text-slate-500">Totale elementi: {totalElements}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Colore</Label>
                  <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
                    <SelectTrigger data-testid="order-color-select"><SelectValue placeholder="Scala VITA" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {(options.colors || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Scadenza</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="order-deadline-trigger" className="w-full justify-start font-normal">
                        <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" />
                        {deadline ? format(deadline, "dd MMM yy", { locale: it }) : "Data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={deadline} onSelect={setDeadline} disabled={(date) => isNonWorkingDay(date, closures) || isBefore(startOfDay(date), urgency ? startOfDay(new Date()) : minDate)} initialFocus />
                    </PopoverContent>
                  </Popover>
                  {!urgency ? (
                    <p className="text-xs text-slate-400" data-testid="deadline-hint">Min. {minDays} gg lavorativi · dal {format(minDate, "dd/MM/yyyy")}</p>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium" data-testid="deadline-hint">Urgenza richiesta: puoi scegliere qualsiasi data. Il laboratorio valuterà la fattibilità.</p>
                  )}
                  {upcomingClosuresLabel(closures) && <p className="text-xs text-amber-700" data-testid="closures-hint">Laboratorio chiuso: {upcomingClosuresLabel(closures)} (date non selezionabili)</p>}
                </div>
              </div>
              {options.urgency_enabled !== false && (
              <div className={`rounded-lg border p-3 flex items-start gap-3 transition-colors ${urgency ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50/60"}`} data-testid="urgency-banner">
                <Zap className={`h-5 w-5 mt-0.5 shrink-0 ${urgency ? "text-amber-600" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">Richiedi urgenza</p>
                  <p className="text-xs text-slate-500">Ti serve prima dei tempi minimi? Attiva l'urgenza per scegliere una data più ravvicinata; l'ordine verrà segnalato al laboratorio come urgente.</p>
                </div>
                <Switch data-testid="urgency-toggle" checked={urgency} onCheckedChange={(v) => setUrgency(!!v)} className="mt-0.5" />
              </div>
              )}
              <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/50 p-3">
                <Label className="flex items-center gap-1.5 text-teal-800"><Box className="h-4 w-4" /> File STL / Progetto 3D <span className="text-xs font-normal text-teal-600">(STL, DCM, ZIP)</span></Label>
                <FileDropzone
                  files={attachFiles}
                  setFiles={setAttachFiles}
                  disabled={saving}
                  allowedExts={["stl", "dcm", "zip"]}
                  maxFiles={12}
                  typeLabel="In questa sezione carica solo file 3D: STL, DCM o ZIP."
                  hint="Fino a 12 file · STL, DCM, ZIP o cartelle non compresse · max 100MB per file"
                />
              </div>
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <Label className="flex items-center gap-1.5"><FileSignature className="h-4 w-4" /> Prescrizione e documenti <span className="text-xs font-normal text-slate-500">(foto, PDF, DOC — cartacei)</span></Label>
                <FileDropzone
                  files={prescrFiles}
                  setFiles={setPrescrFiles}
                  disabled={saving}
                  allowedExts={["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "pdf", "doc", "docx", "xml", "txt"]}
                  typeLabel="In questa sezione carica solo foto o documenti: JPEG, PNG, PDF, DOC."
                  hint="Foto e documenti: JPEG, PNG, PDF, DOC, XML · max 100MB"
                />
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea data-testid="order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Indicazioni aggiuntive..." rows={3} />
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <Checkbox id="privacy-consent" data-testid="order-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                <label htmlFor="privacy-consent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                  Dichiaro di essere autorizzato al trattamento dei dati del paziente e di aver preso visione dell'<a href="/privacy" target="_blank" rel="noreferrer" className="text-teal-600 underline">Informativa sulla Privacy</a>.
                </label>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-col gap-2 items-stretch">
              <UploadProgress progress={uploadProgress} />
              <Button data-testid="submit-order-button" onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 sm:self-end">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Invia ordine
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="flex justify-center mb-10">
        <Button data-testid="new-order-button" onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700 h-16 px-14 text-lg font-semibold rounded-xl shadow-lg shadow-teal-600/25 transition-transform hover:-translate-y-0.5">
          <Plus className="h-6 w-6 mr-2" /> Nuovo ordine
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <StatCard icon={Package} label="Ordini totali" value={stats.total} color="text-teal-600" />
        <StatCard icon={Clock} label="In lavorazione" value={stats.lavorazione} color="text-amber-600" />
        <StatCard icon={FileStack} label="Completati" value={stats.completato} color="text-emerald-600" />
      </div>

      {completedOrders.length > 0 && (
        <div data-testid="completed-banner" className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4">
          <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-emerald-600 text-white shrink-0"><CheckCircle2 className="h-5 w-5" /></span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-emerald-800">{completedOrders.length === 1 ? "Un ordine è stato completato!" : `${completedOrders.length} ordini sono stati completati!`}</p>
            <p className="text-sm text-emerald-700">{completedOrders.map((o) => o.patient_name).join(", ")} — pronto per il ritiro.</p>
          </div>
          <Button size="sm" data-testid="ack-completed-button" onClick={() => ackBanner(completedOrders, "ack-completed")} disabled={ackingBanner} className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
            {ackingBanner ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Ho visto
          </Button>
        </div>
      )}

      {updatedOrders.length > 0 && (
        <div data-testid="update-banner" className="mb-4 flex items-start gap-3 rounded-xl border border-indigo-300 bg-indigo-50 p-4">
          <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-indigo-600 text-white shrink-0"><Bell className="h-5 w-5" /></span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-indigo-800">{updatedOrders.length === 1 ? "Aggiornamento dal laboratorio" : `Aggiornamenti dal laboratorio su ${updatedOrders.length} ordini`}</p>
            <p className="text-sm text-indigo-700">{updatedOrders.map((o) => o.patient_name).join(", ")} — nuovi file/aggiornamenti disponibili. Aprili per vedere.</p>
          </div>
          <Button size="sm" variant="outline" data-testid="ack-update-banner-button" onClick={() => ackBanner(updatedOrders, "ack-lab-update")} disabled={ackingBanner} className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 shrink-0">
            {ackingBanner ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Ho visto
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      ) : orders.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Package className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-4 font-medium text-slate-700">Nessun ordine ancora</p>
          <p className="text-sm text-slate-500">Crea il tuo primo ordine con il pulsante "Nuovo ordine".</p>
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca paziente o n° ordine..." className="pl-9 bg-white" />
            </div>
            <div className="inline-flex self-end sm:self-auto rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
              <button
                type="button"
                data-testid="view-grid-button"
                onClick={() => setView("grid")}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "grid" ? "bg-teal-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
              >
                <LayoutGrid className="h-4 w-4" /> Griglia
              </button>
              <button
                type="button"
                data-testid="view-list-button"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "list" ? "bg-teal-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
              >
                <List className="h-4 w-4" /> Lista
              </button>
            </div>
          </div>

          {view === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-7" data-testid="orders-grid">
              {sortedOrders.map((o, i) => (
                <motion.div key={o.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card data-testid="order-card" onClick={() => navigate(`/orders/${o.id}`)} className="p-8 cursor-pointer border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200">
                    <div className="flex items-start justify-between">
                      <span className="text-sm font-semibold uppercase tracking-wider text-slate-400">{o.order_number}</span>
                      <StatusBadge status={o.status} />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold mt-3 text-slate-900">{o.patient_name}</h3>
                    <p className="text-base text-slate-500 mt-1">
                      {o.macro_category || o.work_type}
                      {o.material && <span className="text-slate-400"> · {o.material}</span>}
                    </p>
                    {o.color && <p className="text-sm text-slate-400 mt-1">Colore: {o.color}</p>}
                    <div className="mt-6">
                      <OrderProgressBar order={o} />
                    </div>
                    <div className="mt-6 flex items-center justify-between text-sm text-slate-400 border-t border-slate-100 pt-4">
                      <span>Scadenza: {formatDate(o.deadline)}</span>
                      <span>{o.files?.length || 0} file</span>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="border-slate-200 overflow-x-auto">
              <Table data-testid="client-orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Ordine</TableHead>
                    <TableHead>Paziente</TableHead>
                    <TableHead>Lavoro</TableHead>
                    <TableHead><SortHeader label="Inserito" field="created_at" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="client-sort-inserito" /></TableHead>
                    <TableHead><SortHeader label="Scadenza" field="deadline" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="client-sort-scadenza" /></TableHead>
                    <TableHead>Quantità</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrders.map((o) => (
                    <TableRow key={o.id} data-testid="client-order-row" onClick={() => navigate(`/orders/${o.id}`)} className="hover:bg-slate-50 cursor-pointer">
                      <TableCell className="font-mono text-xs font-medium text-teal-600">{o.order_number}</TableCell>
                      <TableCell className="font-medium text-slate-800">{o.patient_name}</TableCell>
                      <TableCell>
                        <div className="text-slate-800">{o.macro_category || o.work_type}</div>
                        {o.material && <div className="text-xs text-slate-400">{o.material}{o.color ? ` · ${o.color}` : ""}</div>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(o.created_at)}</TableCell>
                      <TableCell className="text-sm text-slate-500"><DeadlineCell order={o} /></TableCell>
                      <TableCell className="text-sm font-medium text-slate-700">{o.quantity || "—"}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

function SortHeader({ label, field, sortField, sortDir, onClick, testid }) {
  const active = sortField === field;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onClick(field)}
      className={`inline-flex items-center gap-1 select-none transition-colors ${active ? "text-teal-600" : "text-slate-500 hover:text-slate-800"}`}
    >
      {label}
      {active ? (sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />}
    </button>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-6 border-slate-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="font-heading text-3xl font-semibold mt-1 text-slate-900">{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
    </Card>
  );
}
